import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bath, Bed, Car, ExternalLink, Loader2, Lock, Phone, Ruler } from "lucide-react";
import { getPropertyProfile, getPropertyProfilePrivate } from "../../../api/collingsData";
import { formatMelbourneShortDate } from "../../../utils/melbourneTime";
import { KIND_COLORS, KIND_LABELS } from "../components/PropertyMapView";
import { CrmPersonLine, HeldByLine, OwnerContactList } from "../../dashboard/components/RelistingOwnerContacts";

/**
 * One property's permanent profile (`/collings-data-properties/property/:id`).
 *
 * Christian Gravias, Monday 12573204628 (26 Oct 2026): "one permanent property
 * profile per property, with all relevant information from our different
 * sources linked to that profile", "If an agent searches an address, DOMA
 * should bring together all the permitted information we hold about that
 * property. We shouldn't need agents to know which database the information
 * came from." So the page shows the property once: its facts and photos, where
 * it is in the market now, every campaign and sale we hold (Homer, our scraped
 * data and the CRM's own, in one history, with nothing labelled by source), the
 * owners on file, and the CRM listing and Database rows the viewer may open.
 * The old Homer, Collings Data and On file links all land here.
 *
 * Christian, Monday 13354971743 (27 Oct 2026): "Public and private property
 * information remain separated even though they share the same underlying
 * property identity." The page loads the public profile and, as a separate
 * request behind its own gate, the private layer (owners on file, CRM listing,
 * Database rows). The private part sits in one clearly labelled "Private to
 * Collings" panel; if that request is refused the page still shows the public
 * profile.
 *
 * Christian, same item (28 Oct 2026): "3/15 Mcewan is in our pipeline but the
 * data isnt feeding to our public property search". The panel also lists the
 * agents' own people for the address: Database owners and pipeline seller
 * leads the viewer may see, with the agent, list or stage and the last note.
 * A colleague's private Database shows only as "Also in X's Database".
 */

const HISTORY_LABELS = { ...KIND_LABELS, listed: "Listed" };
const HISTORY_COLORS = { ...KIND_COLORS, listed: KIND_COLORS.collings };

function portalName(url) {
    try {
        const host = new URL(url).hostname.replace(/^www\./, "");
        if (host.includes("realestate.com.au")) return "realestate.com.au";
        if (host.includes("domain.com.au")) return "domain.com.au";
        return host;
    } catch {
        return "Listing";
    }
}

const Fact = ({ label, value }) =>
    value ? (
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 text-sm last:border-0">
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">{value}</span>
        </div>
    ) : null;

const Card = ({ title, children, tone = "default" }) => (
    <div className={`rounded-xl border p-5 ${tone === "owner" ? "border-teal-100 bg-teal-50/60" : "border-gray-200 bg-white"}`}>
        <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">{title}</h2>
        {children}
    </div>
);

const SubHeading = ({ children }) => (
    <h3 className="mb-1 mt-3 text-xs font-bold uppercase tracking-wide text-gray-500 first:mt-0">{children}</h3>
);

const PublicPropertyProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [row, setRow] = useState(null);
    const [error, setError] = useState(null);
    const [privateLayer, setPrivateLayer] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setRow(null);
        setError(null);
        setPrivateLayer(null);
        getPropertyProfile(id)
            .then((res) => !cancelled && setRow(res.data))
            .catch((err) => {
                if (cancelled) return;
                setError(err.response?.status === 404 ? "This property profile does not exist." : "Could not load this property.");
            });
        // Refused or failed: the public profile still shows, without the private panel.
        getPropertyProfilePrivate(id)
            .then((res) => !cancelled && setPrivateLayer(res.data))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [id]);

    const back = (
        <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#55C5D0]"
        >
            <ArrowLeft className="h-4 w-4" /> Back
        </button>
    );

    if (error) {
        return (
            <div className="mx-auto max-w-5xl space-y-4 p-4">
                {back}
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">{error}</div>
            </div>
        );
    }
    if (!row) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-[#55C5D0]" />
            </div>
        );
    }

    const color = KIND_COLORS[row.kind] || KIND_COLORS.unlisted;
    const mapQuery = row.latitude != null && row.longitude != null ? `${row.latitude},${row.longitude}` : row.address;
    const photos = (row.images || []).map((i) => i.file).filter(Boolean);
    const owners = privateLayer?.owner_contacts || [];
    const crm = privateLayer?.crm_listings || [];
    const people = privateLayer?.crm_people || [];
    // A Database row listed with its people above opens from there.
    const listed = new Set(people.filter((p) => p.source === "database").map((p) => p.id));
    const records = (privateLayer?.database_records || []).filter((r) => !listed.has(r.id));
    const heldBy = privateLayer?.held_by || [];
    const history = row.history || [];
    const live = row.kind === "buy" || row.kind === "rent";
    // An address we only know from the national address file so far. Say so
    // rather than show empty facts (Christian, 27 Oct 2026: "making it clear
    // where information is unavailable rather than inventing sale prices").
    const bare = !history.length && !photos.length && !row.beds && !row.baths && !row.land_size && !row.property_type;

    return (
        <div className="mx-auto max-w-6xl space-y-5 p-2 sm:p-4">
            {back}

            <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: color }}>
                        {KIND_LABELS[row.kind] || row.kind_label}
                    </span>
                    {row.property_type ? <span className="text-xs capitalize text-gray-500">{row.property_type}</span> : null}
                </div>
                <h1 className="mt-2 text-xl sm:text-2xl font-bold text-gray-900">{row.address}</h1>
                <p className="mt-1 text-lg font-bold text-[#2b8e97]">{row.price_display || (live ? "Contact agent" : "")}</p>
                {row.date_label ? (
                    <p className="mt-0.5 text-sm text-gray-500">
                        {row.date_label}
                        {row.days_on_market != null ? ` · ${row.days_on_market} days on market` : ""}
                    </p>
                ) : null}
                {bare ? (
                    <p className="mt-3 text-sm text-gray-500">
                        No property details, sales or listings on record yet. They will appear here as we collect them.
                    </p>
                ) : (
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
                        <span className="flex items-center gap-1"><Bed className="h-4 w-4" /> {row.beds || 0} bed</span>
                        <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {row.baths || 0} bath</span>
                        <span className="flex items-center gap-1"><Car className="h-4 w-4" /> {row.parkings || 0} car</span>
                        {row.land_size ? (
                            <span className="flex items-center gap-1"><Ruler className="h-4 w-4" /> {Math.round(row.land_size)} m²</span>
                        ) : null}
                    </div>
                )}
            </div>

            {photos.length ? (
                <div className="flex gap-2 overflow-x-auto rounded-xl border border-gray-200 bg-white p-2">
                    {photos.map((src) => (
                        <img
                            key={src}
                            src={src}
                            alt=""
                            loading="lazy"
                            className="h-40 w-60 shrink-0 rounded-lg object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                    ))}
                </div>
            ) : null}

            <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-5">
                    {owners.length || crm.length || records.length || people.length || heldBy.length ? (
                        <Card
                            tone="owner"
                            title={
                                <span className="inline-flex items-center gap-1.5">
                                    <Lock className="h-3.5 w-3.5" /> Private to Collings
                                    <span className="font-semibold normal-case tracking-normal text-gray-400">
                                        · staff only, never on a public profile
                                    </span>
                                </span>
                            }
                        >
                            {owners.length ? (
                                <>
                                    <SubHeading>People on file</SubHeading>
                                    <p className="mb-1 text-[11px] text-gray-500">
                                        From our own records, not a title search. Check consent and the Do Not Call
                                        Register before marketing.
                                    </p>
                                    <OwnerContactList contacts={owners} />
                                </>
                            ) : null}
                            {people.length || heldBy.length ? (
                                <>
                                    <SubHeading>Our Database and pipeline</SubHeading>
                                    {people.map((p) => (
                                        <CrmPersonLine key={`${p.source}-${p.id}`} p={p} onOpen={navigate} />
                                    ))}
                                    <HeldByLine heldBy={heldBy} />
                                </>
                            ) : null}
                            {crm.length || records.length ? <SubHeading>In the CRM</SubHeading> : null}
                            {crm.map((c) => (
                                <button
                                    key={`crm-${c.id}`}
                                    type="button"
                                    onClick={() => navigate(c.url)}
                                    className="flex w-full items-center justify-between gap-3 border-b border-teal-100/60 py-2 text-left text-sm last:border-0 hover:text-[#2b8e97]"
                                >
                                    <span className="font-semibold">Listing{c.status ? ` · ${c.status}` : ""}</span>
                                    <span className="text-xs text-gray-500">{c.agent_name}</span>
                                </button>
                            ))}
                            {records.map((r) => (
                                <button
                                    key={`db-${r.id}`}
                                    type="button"
                                    onClick={() => navigate(r.url)}
                                    className="flex w-full items-center justify-between gap-3 border-b border-teal-100/60 py-2 text-left text-sm last:border-0 hover:text-[#2b8e97]"
                                >
                                    <span className="font-semibold">Database{r.list_name ? ` · ${r.list_name}` : ""}</span>
                                    <span className="text-xs text-gray-500">{r.agent_name}</span>
                                </button>
                            ))}
                        </Card>
                    ) : null}

                    <Card title="Property">
                        <Fact label="Last sold" value={(row.last_sold_label || "").replace(/^Last sold /, "")} />
                        <Fact label="Council" value={row.lga} />
                        <Fact label="Suburb" value={`${row.suburb} ${row.state} ${row.postal_code}`.trim()} />
                    </Card>

                    {row.agency_name || row.agent_name || (row.listing_urls || []).length ? (
                        <Card title={live ? "Listing agency" : "Last listing agency"}>
                            <Fact label="Agency" value={row.agency_name} />
                            <Fact label="Agent" value={row.agent_name} />
                            {row.agent_phone ? (
                                <div className="flex justify-between gap-4 py-2 text-sm">
                                    <span className="text-gray-500">Phone</span>
                                    <a href={`tel:${row.agent_phone}`} className="flex items-center gap-1 font-semibold text-[#2b8e97] hover:underline">
                                        <Phone className="h-3.5 w-3.5" /> {row.agent_phone}
                                    </a>
                                </div>
                            ) : null}
                            {(row.listing_urls || []).length ? (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {row.listing_urls.map((u) => (
                                        <a
                                            key={u}
                                            href={u}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 rounded-lg bg-[#E8F7F8] px-3 py-1.5 text-xs font-semibold text-[#2b8e97] hover:bg-[#d9f1f3]"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" /> {portalName(u)}
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                        </Card>
                    ) : null}

                    {history.length ? (
                        <Card title="Property history">
                            {history.map((h, i) => (
                                <div
                                    key={`${h.kind}-${h.date}-${i}`}
                                    className="flex items-center justify-between gap-3 border-b border-gray-100 py-2 text-sm last:border-0"
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span
                                            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                                            style={{ backgroundColor: HISTORY_COLORS[h.kind] || KIND_COLORS.unlisted }}
                                        >
                                            {HISTORY_LABELS[h.kind] || h.kind}
                                        </span>
                                        <span className="truncate">{h.price_label}</span>
                                        {h.listing_url ? (
                                            <a
                                                href={h.listing_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Open the listing"
                                                className="shrink-0 text-gray-400 hover:text-[#2b8e97]"
                                            >
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        ) : null}
                                    </span>
                                    <span className="shrink-0 text-right text-xs text-gray-500">
                                        {formatMelbourneShortDate(h.date)}
                                        {h.agency_name ? ` · ${h.agency_name}` : ""}
                                    </span>
                                </div>
                            ))}
                        </Card>
                    ) : null}
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <iframe
                        title="Property location"
                        className="h-80 w-full lg:h-full lg:min-h-[420px]"
                        loading="lazy"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`}
                    />
                </div>
            </div>
            {row.attribution ? <p className="px-1 text-[11px] text-gray-400">{row.attribution}</p> : null}
        </div>
    );
};

export default PublicPropertyProfilePage;
